import { useMemo } from 'react'

import { Code } from '@connectrpc/connect'
import type { GenerationSaleSubKind } from '@ebalance/proto/bfftypes/v1/plan_submission_pb'
import { useTranslation } from 'react-i18next'

import { CrudModalLayout } from '~/components/common/CrudModalLayout'
import { useCrudModalForm } from '~/hooks/useCrudModalForm'
import { todayCivilDate } from '~/models/civilDate'
import { isActiveOn } from '~/models/validPeriod'
import {
  convertBgMasterListItems,
  convertListBgMastersResponse,
  useListBgMasters,
} from '~/pages/AdminOcctoMaster/tabs/BgMaster/hooks/useListBgMasters'
import { GenerationSalesPlanMasterFormFields } from '~/pages/AdminOcctoMaster/tabs/GenerationSalesPlanMaster/components/GenerationSalesPlanMasterFormFields'
import { useCreateGenerationSalesMaster } from '~/pages/AdminOcctoMaster/tabs/GenerationSalesPlanMaster/hooks/useCreateGenerationSalesMaster'
import { useGetGenerationSalesPlanMasterFormOptions } from '~/pages/AdminOcctoMaster/tabs/GenerationSalesPlanMaster/hooks/useGetGenerationSalesPlanMasterFormOptions'
import { planCategoryLabel } from '~/pages/AdminOcctoMaster/tabs/GenerationSalesPlanMaster/models/planCategory'
import {
  type GenerationSalesMasterFormValues,
  generationSalesMasterFormSchema,
} from '~/pages/AdminOcctoMaster/tabs/GenerationSalesPlanMaster/models/schema'
import { resolveGenerationSalesMasterDetailedError } from '~/pages/AdminOcctoMaster/tabs/GenerationSalesPlanMaster/models/serverError'
import { convertPlantMasterListItems } from '~/pages/AdminOcctoMaster/tabs/PlantMaster/hooks/useListPlantMasters'
import { useGetSubmitterMaster } from '~/pages/AdminOcctoMaster/tabs/SubmitterMaster/hooks/useGetSubmitterMaster'
import {
  convertListSubmitterMastersResponse,
  convertSubmitterMasterListItems,
  useListSubmitterMasters,
} from '~/pages/AdminOcctoMaster/tabs/SubmitterMaster/hooks/useListSubmitterMasters'
import { useListTradingPartners } from '~/pages/AdminOcctoMaster/tabs/TradingPartner/hooks/useListTradingPartners'
import { deriveBgAddDisabledReason } from '~/pages/AdminOcctoMaster/tabs/_common/models/deriveBgAddDisabledReason'
import { findBgsOutOfPeriod } from '~/pages/AdminOcctoMaster/tabs/_common/models/planMasterBgValidation'

type Props = {
  isOpen: boolean
  onClose: () => void
  /**
   * 一覧の計画区分フィルタ選択値。Create request にそのまま設定し、
   * モーダル内では disabled 表示のみ行う (AC-05、変更不可)。
   */
  planCategory: GenerationSaleSubKind
}

/**
 * 発電販売計画マスタ 新規登録モーダル。
 *
 * React Hook Form + Zod で validation。
 * submitterMasterId は計画提出者マスタのドロップダウンで選択する。
 * submitterCode は選択した提出者から自動取得する。
 * codes / bgs / plants は後から Edit モーダルで追加する (Create 時は空でも可)。
 * onSuccess 後にモーダルを閉じて form をリセットする。
 */
export const CreateGenerationSalesPlanMasterModal = ({
  isOpen,
  onClose,
  planCategory,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const { mutateAsync, isPending } = useCreateGenerationSalesMaster()
  // 集約 RPC: 発電区分でフィルタ済みの submitters / bgs を取得 (区分 client filter 不要)
  const { data: formOptions, isPending: isLoadingFormOptions } =
    useGetGenerationSalesPlanMasterFormOptions()
  // codes section 用: 全件フェッチ (BG7 スコープ外、既存 hook 維持)
  const { data: allBgMastersData } = useListBgMasters()
  const { data: allSubmittersData } = useListSubmitterMasters()
  const { data: tradingPartnersData } = useListTradingPartners()

  // 集約 RPC の submitters (発電区分フィルタ済み) を変換: 計画提出者 select 用
  const submitters = useMemo(
    () =>
      formOptions !== undefined
        ? convertSubmitterMasterListItems(formOptions.submitters)
        : [],
    [formOptions],
  )
  // 集約 RPC の bgs (発電区分フィルタ済み) を変換: 所属BG section 用 (area narrow のみ client-side)
  const bgMasters = useMemo(
    () =>
      formOptions !== undefined
        ? convertBgMasterListItems(formOptions.bgs)
        : [],
    [formOptions],
  )
  // 集約 RPC の plants (区分フィルタなし全件、AC-05) を変換: 所属発電所 section 用
  // (area narrow は client-side、AC-03)
  const plantMasters = useMemo(
    () =>
      formOptions !== undefined
        ? convertPlantMasterListItems(formOptions.plants)
        : [],
    [formOptions],
  )
  // codes section 用: 全件 BG / submitter (BG7 スコープ外、既存 fetch 維持)。
  // list クエリ取得中は undefined (fallback 解決の「(削除済み)」誤表示防止、
  // PR #5920 レビュー指摘)。
  const allBgMasters = useMemo(
    () =>
      allBgMastersData !== undefined
        ? convertListBgMastersResponse(allBgMastersData)
        : undefined,
    [allBgMastersData],
  )
  const allSubmitters = useMemo(
    () =>
      allSubmittersData !== undefined
        ? convertListSubmitterMastersResponse(allSubmittersData)
        : undefined,
    [allSubmittersData],
  )
  const tradingPartners = useMemo(
    () => tradingPartnersData?.items ?? [],
    [tradingPartnersData],
  )

  const {
    control,
    errors,
    serverError,
    handleClose,
    handleSave: hookHandleSave,
    setValue,
    watch,
    setError,
    getValues,
    isDirty,
  } = useCrudModalForm<GenerationSalesMasterFormValues>({
    schema: generationSalesMasterFormSchema,
    defaultValues: {
      submitterMasterId: '',
      submitterCode: '',
      note: '',
      codes: [],
      bgs: [],
      plants: [],
      validFrom: todayCivilDate(),
      validTo: undefined,
    },
    onSubmit: async (values) => {
      await mutateAsync({
        submitterMasterId: values.submitterMasterId,
        submitterCode: values.submitterCode,
        note:
          values.note !== undefined && values.note !== ''
            ? values.note
            : undefined,
        // codes / bgs / plants は fieldArray バインドで入力された値を送出する (RG6)
        codes: values.codes.map((c) => ({
          code: c.code,
          codeName: c.codeName,
          isInternal: c.isInternal,
          validFrom: c.validFrom,
          validTo: c.validTo,
        })),
        bgs: values.bgs.map((b) => ({
          bgMasterId: b.bgMasterId,
          validFrom: b.validFrom,
          validTo: b.validTo,
        })),
        plants: values.plants.map((p) => ({
          plantMasterId: p.plantMasterId,
          validFrom: p.validFrom,
          validTo: p.validTo,
        })),
        // 一覧の計画区分フィルタ選択値をそのまま設定する (AC-05、immutable のため
        // モーダル内フォームでは編集不可)。
        planCategory,
        validFrom: values.validFrom,
        validTo: values.validTo,
      })
    },
    onClose,
    customErrorKeys: {
      [Code.Aborted]:
        'page:adminOcctoMaster.generationSalesPlanMaster.serverError.aborted',
      [Code.NotFound]:
        'page:adminOcctoMaster.generationSalesPlanMaster.serverError.notFound',
      [Code.InvalidArgument]:
        'page:adminOcctoMaster.generationSalesPlanMaster.serverError.invalidArgument',
    },
    unknownErrorKey:
      'page:adminOcctoMaster.generationSalesPlanMaster.serverError.unknown',
    // AC-08: 計画提出者 × 計画区分の重複は spec の動的文言 (submitterCode /
    // planCategoryLabel) を含む。 registry の SUBMITTER_PLAN_CATEGORY_ALREADY_
    // EXISTS entry は submit 済み form 値 (values, N3 stale-closure 対策) と
    // 一覧フィルタの計画区分 (planCategory prop、モーダル内では編集不可の安定値)
    // を合成した値を補間値として渡す。
    resolveDetailedError: (err, t, values) =>
      resolveGenerationSalesMasterDetailedError(err, t, {
        submitterCode: values.submitterCode,
        planCategoryLabel: planCategoryLabel(planCategory),
      }),
  })

  // 選択した計画提出者の areaCode を解決して BG/Codes の候補フィルタに使う
  const selectedSubmitterMasterId = watch('submitterMasterId')
  const selectedSubmitterCode = watch('submitterCode')
  const submitterAreaCode = useMemo(() => {
    const code = selectedSubmitterCode
    if (code === '' || code === undefined) return undefined
    return submitters.find((s) => s.submitterCode === code)?.areaCode
  }, [selectedSubmitterCode, submitters])

  // AC-06: 選択中の計画提出者の所属BG (members) を個別取得する。
  // 集約 RPC (GetGenerationSalesPlanMasterFormOptionsResponse.submitters) の
  // SubmitterMasterListItem は members field を持たないため (実物確認済み、
  // deriveBgAddDisabledReason の doc comment 参照)、既存の
  // useGetSubmitterMaster (full entity) を選択時のみ呼ぶ。
  const { data: selectedSubmitterDetail } = useGetSubmitterMaster(
    selectedSubmitterMasterId !== '' ? selectedSubmitterMasterId : undefined,
  )
  const bgAddDisabledReason = useMemo(
    () =>
      deriveBgAddDisabledReason(
        selectedSubmitterCode !== '' ? selectedSubmitterCode : undefined,
        selectedSubmitterDetail?.submitterMaster?.members,
        bgMasters,
      ),
    [selectedSubmitterCode, selectedSubmitterDetail, bgMasters],
  )

  // AC-01: codes[] 配列全体の min(1) 違反 (field-level 表示)。
  const codesFieldError = errors.codes as
    | { root?: { message?: string }; message?: string }
    | undefined
  const codesErrorMessage =
    codesFieldError?.root?.message ??
    (typeof codesFieldError?.message === 'string'
      ? codesFieldError.message
      : undefined)

  // 選択済み所属 BG が validFrom narrow 後の候補外 (= 有効期間外) なら
  // submit を block する。fallback option で UI 上は残るが、そのまま save
  // すると失効 BG を含むレコードが RPC 送信される silent invalid submission
  // を防ぐ (物理削除は AC-06 尊重で除外、helper 参照)。
  const validateBgsPeriodBeforeSubmit = (): boolean => {
    const values = getValues()
    const recordValidFrom = values.validFrom
    const bgCandidates = bgMasters.filter(
      (bg) =>
        (submitterAreaCode === undefined ||
          bg.areaCode === submitterAreaCode) &&
        (recordValidFrom === undefined ||
          isActiveOn(bg.validFrom, bg.validTo, recordValidFrom)),
    )
    const outOfPeriod = findBgsOutOfPeriod(
      values.bgs ?? [],
      bgCandidates,
      // allBgMastersData 未取得時は undefined を渡し、helper 側で
      // fail-closed (候補外を全て有効期間外扱い) にする。
      allBgMastersData !== undefined ? allBgMasters : undefined,
    )
    if (outOfPeriod.length === 0) return true
    for (const { index } of outOfPeriod) {
      setError(`bgs.${index}.bgMasterId`, {
        message: t(
          'page:adminOcctoMaster.generationSalesPlanMaster.form.bgMasterOutOfPeriod',
        ),
      })
    }
    return false
  }

  // 期間外 BG の pre-check を hook の handleSave の前に挟む。
  const handleSave = (): void => {
    if (!validateBgsPeriodBeforeSubmit()) return
    hookHandleSave()
  }

  return (
    <CrudModalLayout
      handleSave={handleSave}
      isDirty={isDirty}
      isOpen={isOpen}
      isPending={isPending}
      onClose={handleClose}
      serverError={serverError}
      // design (`design/admin/occto.html` #pm-edit-modal) は `.ecl-modal--lg`
      // (gen-plan / proc-plan は同 modal を label 切替で共有)。
      size="lg"
      title={t(
        'page:adminOcctoMaster.generationSalesPlanMaster.modal.createTitle',
      )}
    >
      <GenerationSalesPlanMasterFormFields
        allBgMasters={allBgMasters}
        bgAddDisabledReason={bgAddDisabledReason}
        bgMasters={bgMasters}
        codesErrorMessage={codesErrorMessage}
        control={control}
        errors={errors}
        isEdit={false}
        isLoadingFormOptions={isLoadingFormOptions}
        planCategory={planCategory}
        plantMasters={plantMasters}
        selfSubmitterCode={selectedSubmitterCode}
        setValue={setValue}
        submitterAreaCode={submitterAreaCode}
        submitterList={submitters}
        submitters={allSubmitters}
        tradingPartners={tradingPartners}
      />
    </CrudModalLayout>
  )
}
